import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

export interface TranslationError {
    type:
        | 'missing_key'
        | 'loading_failed'
        | 'network_error'
        | 'parse_error'
        | 'unknown';
    message: string;
    key?: string;
    language?: string;
    originalError?: any;
    timestamp: number;
}

@Injectable({
    providedIn: 'root',
})
export class TranslationErrorHandlerService {
    private errorHistory: TranslationError[] = [];
    private readonly MAX_ERROR_HISTORY = 50;

    /**
     * Handle missing translation key
     */
    handleMissingTranslation(key: string, language?: string): string {
        const error: TranslationError = {
            type: 'missing_key',
            message: `Translation missing for key: ${key}`,
            key,
            language,
            timestamp: Date.now(),
        };

        this.logError(error);
        return this.createFallbackText(key);
    }

    /**
     * Handle translation file loading error
     */
    handleLoadingError(language: string, error: any): Observable<never> {
        const translationError: TranslationError = {
            type: this.determineErrorType(error),
            message: `Failed to load translations for language: ${language}`,
            language,
            originalError: error,
            timestamp: Date.now(),
        };

        this.logError(translationError);

        // Return an observable that emits an error
        return throwError(() => translationError);
    }

    /**
     * Handle parameter error in translation
     */
    handleParameterError(
        key: string,
        params: any,
        error: any,
        language?: string
    ): string {
        const translationError: TranslationError = {
            type: 'parse_error',
            message: `Translation parameter error for key: ${key}`,
            key,
            language,
            originalError: error,
            timestamp: Date.now(),
        };

        this.logError(translationError);

        // Try to return translation without parameters
        return this.createFallbackText(key);
    }

    /**
     * Handle network error during translation loading
     */
    handleNetworkError(
        language: string,
        error: HttpErrorResponse
    ): Observable<never> {
        const translationError: TranslationError = {
            type: 'network_error',
            message: `Network error loading translations for ${language}: ${error.status} ${error.statusText}`,
            language,
            originalError: error,
            timestamp: Date.now(),
        };

        this.logError(translationError);
        return throwError(() => translationError);
    }

    /**
     * Create user-friendly fallback text from translation key
     */
    private createFallbackText(key: string): string {
        if (!key) return '[Missing Translation]';

        // Split by dots and take the last part
        const parts = key.split('.');
        const lastPart = parts[parts.length - 1];

        // Convert camelCase to readable text
        const readable = lastPart
            .replace(/([A-Z])/g, ' $1') // Add space before capital letters
            .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
            .replace(/_/g, ' ') // Replace underscores with spaces
            .trim();

        return readable || '[Translation Key Error]';
    }

    /**
     * Determine error type from error object
     */
    private determineErrorType(error: any): TranslationError['type'] {
        if (error instanceof HttpErrorResponse) {
            return 'network_error';
        }

        if (error?.name === 'SyntaxError' || error?.message?.includes('JSON')) {
            return 'parse_error';
        }

        if (error?.status === 404) {
            return 'loading_failed';
        }

        return 'unknown';
    }

    /**
     * Log error to console and store in history
     */
    private logError(error: TranslationError): void {
        // Log to console with appropriate level
        switch (error.type) {
            case 'missing_key':
                console.warn(`[Translation Warning] ${error.message}`, error);
                break;
            case 'loading_failed':
            case 'network_error':
            case 'parse_error':
                console.error(`[Translation Error] ${error.message}`, error);
                break;
            default:
                console.error(
                    `[Translation Unknown Error] ${error.message}`,
                    error
                );
        }

        // Store in error history
        this.errorHistory.unshift(error);

        // Limit history size
        if (this.errorHistory.length > this.MAX_ERROR_HISTORY) {
            this.errorHistory = this.errorHistory.slice(
                0,
                this.MAX_ERROR_HISTORY
            );
        }
    }

    /**
     * Get error history
     */
    getErrorHistory(): TranslationError[] {
        return [...this.errorHistory];
    }

    /**
     * Clear error history
     */
    clearErrorHistory(): void {
        this.errorHistory = [];
    }

    /**
     * Get recent errors of specific type
     */
    getRecentErrors(
        type?: TranslationError['type'],
        limit: number = 10
    ): TranslationError[] {
        let errors = this.errorHistory;

        if (type) {
            errors = errors.filter((error) => error.type === type);
        }

        return errors.slice(0, limit);
    }

    /**
     * Check if there are critical errors that might affect user experience
     */
    hasCriticalErrors(): boolean {
        const recentErrors = this.errorHistory.slice(0, 10);
        const criticalTypes: TranslationError['type'][] = [
            'loading_failed',
            'network_error',
        ];

        return recentErrors.some((error) => criticalTypes.includes(error.type));
    }

    /**
     * Get error statistics
     */
    getErrorStats(): { [key in TranslationError['type']]: number } {
        const stats = {
            missing_key: 0,
            loading_failed: 0,
            network_error: 0,
            parse_error: 0,
            unknown: 0,
        };

        this.errorHistory.forEach((error) => {
            stats[error.type]++;
        });

        return stats;
    }

    /**
     * Create a graceful degradation message for users
     */
    createUserFriendlyErrorMessage(language: string): string {
        return `Unable to load ${
            language === 'fr' ? 'French' : 'English'
        } translations. Using fallback language.`;
    }

    /**
     * Check if error is recoverable
     */
    isRecoverableError(error: TranslationError): boolean {
        return (
            error.type === 'network_error' || error.type === 'loading_failed'
        );
    }

    /**
     * Suggest recovery action for error
     */
    suggestRecoveryAction(error: TranslationError): string {
        switch (error.type) {
            case 'network_error':
                return 'Check your internet connection and try again.';
            case 'loading_failed':
                return 'The translation file may be missing. Please refresh the page.';
            case 'parse_error':
                return 'There may be an issue with the translation file format.';
            case 'missing_key':
                return 'This text is not yet translated.';
            default:
                return 'Please refresh the page or try again later.';
        }
    }
}
